import { TradeDatesApi } from 'tradeDates/api/TradeDatesApi.ts';
import RequestProgressService from 'requestProgress/RequestProgressService.ts';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState.ts';
import { Store } from 'redux';
import { addTradeDates } from 'tradeDates/store/tradeDatesSlice.ts';
import { DateTime } from 'luxon';
import {
    buildLogger,
    Logger,
    LoggingService,
} from '@instinet-analytics/core-lib';

export class TradeDatesService {
    public store: Store | undefined;
    private api: TradeDatesApi;
    private logger: Logger;
    private requestProgressService: RequestProgressService;
    private requested: {
        [symbol: string]: { daysBack: number; daysforward: number };
    } = {};

    public constructor({
        requestProgressService,
        tradeDatesApi,
        loggingService,
    }: {
        requestProgressService: RequestProgressService;
        tradeDatesApi: TradeDatesApi;
        loggingService: LoggingService;
    }) {
        this.api = tradeDatesApi;
        this.requestProgressService = requestProgressService;
        this.logger = buildLogger('TradeDatesService', loggingService);
    }

    public requestTradeDates(
        symbol: string,
        symbology: SymbologyValues,
        ndaysBack: number,
        ndaysForward: number
    ) {
        if (!this.requested[symbol]) {
            this.requested[symbol] = { daysBack: 0, daysforward: 0 };
        }
        if (ndaysBack > 0 && ndaysBack > this.requested[symbol].daysBack) {
            this.requested[symbol].daysBack = ndaysBack;
            this.logger.debug(
                `Requesting ${ndaysBack} days back for ${symbology}/${symbol}`
            );
            this.getTradeDates(symbol, symbology, ndaysBack * -1);
        }
        if (
            ndaysForward > 0 &&
            ndaysForward > this.requested[symbol].daysforward
        ) {
            this.requested[symbol].daysforward = ndaysForward;
            this.logger.debug(
                `Requesting ${ndaysForward} days forward for ${symbology}/${symbol}`
            );
            this.getTradeDates(symbol, symbology, ndaysForward);
        }
    }

    private async getTradeDates(
        symbol: string,
        symbology: SymbologyValues,
        ndays: number
    ) {
        const tradeDates =
            await this.requestProgressService.invokeAndMonitorRequest(
                'getTradeDates',
                (options) =>
                    this.api.getTradingDays(symbol, symbology, ndays, options)
            );

        if (!tradeDates || tradeDates.length === 0) {
            this.logger.debug(
                `No trade dates retrieved for ${symbol}/${symbology}/${ndays}`
            );
            return;
        }

        const mappedTradeDates: Array<{
            index: number;
            date: number;
            flags: string[];
            notes: string;
        }> = tradeDates.map((t) => ({
            index: t.index,
            flags: t.flags,
            notes: t.notes,
            date: toDate(t.date),
        }));

        this.store!.dispatch(
            addTradeDates({ symbol, tradeDates: mappedTradeDates })
        );
    }
}

function toDate(date: string): number {
    return DateTime.fromFormat(date, 'yyyy-MMM-d').toMillis();
}
